import { Branch, Link, Tag } from 'src/components/types';
import { DiffViewMode, FileViewMode } from 'src/redux/diff-settings';
import { RouteComponentProps } from 'src/router/types';
import { PullRequestRenderingLimits } from 'src/types/pull-request';

export type CommitAction = 'commit' | 'delete' | 'rename';

export type Ref = Branch | Tag;
export type RefType = Ref['type'];

export type CommitResults = {
  commitAction: CommitAction;
  commitHash: string;
  commitUrl: string;
  filePath: string;
  pullRequestCreateUrl: string | null | undefined;
};

export type RepositoryPrivilege = 'admin' | 'read' | 'write' | 'none';

export type GroupAccess = Array<{ name: string; team: string }>;

export type Mirror = {
  id: string;
  name: string;
  preferred: boolean;
  url: string;
};

type MirrorResourceLink = Link & {
  name: 'https' | 'http' | 'ssh';
};

export type MirrorCloneResource = {
  available: boolean;
  links: {
    clone: MirrorResourceLink[];
    push?: MirrorResourceLink[];
  };
  mirrorName: string;
  repositoryId: string;
  status: 'AVAILABLE' | 'UNAVAILABLE';
  updatedDate: number;
};

// WatchPrefs is the settings object passed to and returned from the repository email subscriptions endpoint
export type WatchPrefs = {
  commit_status?: boolean;
  commits?: boolean;
  forks?: boolean;
  has_commit_statuses?: boolean;
  has_issues?: boolean;
  has_repo?: boolean;
  has_wiki?: boolean;
  issues?: boolean;
  pullrequests?: boolean;
  wiki?: boolean;
};

export type RouteProps<T = {}> = RouteComponentProps<
  T & {
    repositoryFullSlug: string;
  }
>;

export type LegacyRouteProps<T = {}> = RouteComponentProps<
  T & {
    repositoryOwner: string;
    repositorySlug: string;
  }
>;

export type XferStatus = null | {
  created_on: Date;
  instigator: BB.User | BB.Team;
  recipient: BB.User | BB.Team;
  status: string;
};

export type UpdateSettings = {
  [key: string]: DiffViewMode | FileViewMode | number | boolean;
  diffViewMode: DiffViewMode;
  fileViewMode: FileViewMode;
  tabSize: number;
  isWordDiffEnabled: boolean;
  isColorBlindModeEnabled: boolean;
  shouldIgnoreWhitespace: boolean;
  isAnnotationsEnabled: boolean;
  isSingleFileModeEnabled: boolean;
};

export type PullRequestAttribute = {
  repoUUID: string | null | undefined;
  prID: number | null;
  fileHrefs: string[];
  pullRequestDiffLinesCount: number;
  sidebarWidth: number;
  isMobile: boolean;
  diffSettings: UpdateSettings;
  renderingLimits: PullRequestRenderingLimits | null;
};

export enum REPO_EVENT_SOURCES {
  SETTINGS_DEFAULT_REVIEWERS_SCREEN = 'repositorySettingsDefaultReviewersScreen',
  SETTINGS_MERGE_STRATEGIES_SCREEN = 'repositorySettingsMergeStrategiesScreen',
  SETTINGS_ACCESS_KEYS_SCREEN = 'repositorySettingsAccessKeysScreen',
  SETTINGS_ADD_DEFAULT_REVIEWERS_MODAL = 'repositorySettingsAddReviewersModal',
  SETTINGS_CREATE_ACCESS_KEY_MODAL = 'repositorySettingsAddSshKeyModal',
  SETTINGS_DELETE_ACCESS_KEY_MODAL = 'repositorySettingsDeleteSshKeyModal',
  SETTINGS_VIEW_ACCESS_KEY_MODAL = 'repositorySettingsViewSshKeyModal',
  MERGE_STRATEGIES_INHERITANCE_TOGGLE = 'repositorySettingsMergeStrategyInheritance',
  SETTINGS_BRANCHING_MODEL_SCREEN = 'repositorySettingsBranchingModelScreen',
  SETTINGS_PERMISSIONS_SCREEN = 'repositorySettingsPermissionsScreen',
  DETAILS_SCREEN = 'repositoryDetailsScreen',
  TRANSFER_MODAL = 'transferRepositoryModal',
  BRANCH_SCREEN = 'repositoryBranchScreen',
  BRANCHES_SCREEN = 'branchesScreen',
  BRANCH_SYNC_MODAL = 'branchSyncModal',
  SETTINGS_PIPELINES_RUNNERS_SCREEN = 'repositorySettingsPipelinesRunnersScreen',
  SETTINGS_PIPELINES_INTEGRATIONS_SCREEN = 'repositorySettingsPipelinesIntegrationsScreen',
  SETTINGS_PIPELINES_SSH_KEYS_SCREEN = 'repositorySettingsPipelinesSshKeysScreen',
  SETTINGS_PIPELINES_DEPLOYMENTS_SCREEN = 'repositorySettingsPipelinesDeploymentsScreen',
  SETTINGS_PIPELINES_VARIABLES_SCREEN = 'repositorySettingsPipelinesRepositoryVariablesScreen',
  SETTINGS_PIPELINES_OPENID_CONNECT_SCREEN = 'repositorySettingsPipeliensOpenIdConnectScreen',
  SETTINGS_PIPELINES_SETTINGS_SCREEN = 'repositorySettingsPipeliensSettingsScreen',
  SETTINGS_PIPELINES_DYNAMIC_PIPELINES_SCREEN = 'repositorySettingsPipelinesDynamicPipeliinesScreen',
  PULL_REQUEST_SCREEN = 'pullRequestScreen',
  SETTINGS_CODE_REVIEWER = 'repositorySettingsCodeReviewerScreen',
  UNKNOWN = 'unknown',
}
